import React, { memo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import styled from 'styled-components';

import {
  useStudentSlackUserQuery,
  useMapSlackUserMutation,
  StudentSlackUserDocument,
} from 'core/graphql';
import { Colors } from 'services/ColorService';

interface ISlackUserConfigProps {
  studentId: string;
}

/**
 * Per-student Slack config (spec 0003 redesign): link the student's Slack user
 * id (+ email) so their DMs mirror into this card. Read + set in one place.
 */
export const SlackUserConfig = memo(({ studentId }: ISlackUserConfigProps) => {
  const { t } = useTranslation();
  const { data } = useStudentSlackUserQuery({
    variables: { studentId },
    fetchPolicy: 'cache-and-network',
  });
  const link = data?.slackUserLink;
  const configured = !!link?.slackUserId;

  const [slackUserId, setSlackUserId] = useState('');
  const [email, setEmail] = useState('');
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setSlackUserId(link?.slackUserId || '');
    setEmail(link?.email || '');
  }, [link?.slackUserId, link?.email]);

  const [save, { loading }] = useMapSlackUserMutation({
    refetchQueries: [
      { query: StudentSlackUserDocument, variables: { studentId } },
    ],
    awaitRefetchQueries: true,
  });

  const onSave = useCallback(async () => {
    if (!slackUserId.trim()) return;
    const payload = (
      await save({
        variables: {
          slackUserId: slackUserId.trim(),
          studentId,
          email: email.trim() || null,
        },
      })
    ).data?.linkSlackUser;
    if (payload?.success) {
      setEditing(false);
      message.success(t('pages.slackConfig.userLinkSuccess'));
    } else {
      message.error(
        payload?.customErrors?.[0]?.message ||
          t('pages.slackConfig.userLinkError'),
      );
    }
  }, [save, slackUserId, email, studentId, t]);

  return (
    <Card>
      <CardTitle>{t('pages.slackConfig.userTitle')}</CardTitle>
      <Hint>{t('pages.slackConfig.userHint')}</Hint>
      {configured && !editing ? (
        <>
          <Linked>
            ✓ {t('pages.slackConfig.linkedUser', { id: link?.slackUserId })}
          </Linked>
          <ChangeBtn type="button" onClick={() => setEditing(true)}>
            {t('pages.slackConfig.change')}
          </ChangeBtn>
        </>
      ) : (
        <>
          <Row>
            <Input
              value={slackUserId}
              placeholder={t('pages.slackConfig.userIdPlaceholder')}
              onChange={(e) => setSlackUserId(e.target.value)}
            />
            <Input
              value={email}
              placeholder={t('pages.slackConfig.emailPlaceholder')}
              onChange={(e) => setEmail(e.target.value)}
            />
            <SaveButton
              type="button"
              disabled={loading || !slackUserId.trim()}
              onClick={onSave}
            >
              {t('pages.slackConfig.save')}
            </SaveButton>
            {configured ? (
              <CancelBtn
                type="button"
                onClick={() => {
                  setSlackUserId(link?.slackUserId || '');
                  setEmail(link?.email || '');
                  setEditing(false);
                }}
              >
                {t('pages.slackConfig.cancel')}
              </CancelBtn>
            ) : null}
          </Row>
          {!configured ? (
            <NotLinked>{t('pages.slackConfig.notLinked')}</NotLinked>
          ) : null}
        </>
      )}
    </Card>
  );
});

const Card = styled.div`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 10px;
  background: ${Colors.White};
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${Colors.Gray};
  margin-bottom: 6px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  min-width: 140px;
  box-sizing: border-box;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

const SaveButton = styled.button`
  border: 1px solid ${Colors.Blue};
  background: ${Colors.Blue};
  color: ${Colors.White};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Linked = styled.div`
  font-size: 12px;
  color: ${Colors.SuccessDefault};
  margin-top: 8px;
`;

const NotLinked = styled.div`
  font-size: 12px;
  color: ${Colors.Gray};
  margin-top: 8px;
`;

const ChangeBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-top: 4px;
  font: inherit;
  font-size: 12px;
  color: ${Colors.Blue};
  cursor: pointer;
`;

const CancelBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: ${Colors.Gray};
  cursor: pointer;
`;
